/**
 * Aba "Conteúdo do Email" do formulário de template — um editor só.
 *
 * Três sub-abas: PREVIEW (iframe seguro, mesmo sandbox do leitor de e-mail,
 * com os valores de exemplo das variáveis já aplicados), HTML (textarea em
 * fonte mono, onde o usuário digita ou cola o HTML) e TEXTO SIMPLES (mesmo
 * textarea, para o `plainTextBody` que o cliente de e-mail usa quando não
 * mostra HTML). O botão "Construir com IA" da toolbar grava em
 * `providerConfig.htmlBody`, que é o que esta aba lê.
 *
 * Substituiu os editores visuais GrapeJS e Unlayer em 2026-09-09. O JSON de
 * design deles (`editorData`) nunca foi persistido — o Zod do backend o
 * descartava — então nada se perdeu na troca.
 */
import React, { useState } from 'react';
import { EmailHtmlFrame, Tabs, Textarea } from '@/shared/components/ui';
import type { TabItem } from '@/shared/components/ui/Tabs';

export interface EmailContentTabProps {
  /** HTML cru do template (`providerConfig.htmlBody`), com `{{1}}`, `{{2}}`… */
  html: string;
  /** HTML já renderizado com os valores de exemplo, para o preview. */
  previewHtml: string;
  onHtmlChange: (html: string) => void;
  /** Versão em texto puro (`providerConfig.plainTextBody`). */
  plainText: string;
  onPlainTextChange: (text: string) => void;
}

type SubTab = 'preview' | 'html' | 'plain-text';

const CAIXA = { backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' } as const;

export const EmailContentTab: React.FC<EmailContentTabProps> = ({
  html, previewHtml, onHtmlChange, plainText, onPlainTextChange,
}) => {
  const [subTab, setSubTab] = useState<SubTab>('preview');

  const tabs: TabItem[] = [
    { id: 'preview', label: 'Preview', icon: 'eye', content: null, 'data-testid': 'template-email-tab-preview' },
    { id: 'html', label: 'HTML', icon: 'code', content: null, 'data-testid': 'template-email-tab-html' },
    { id: 'plain-text', label: 'Texto simples', icon: 'align-left', content: null, 'data-testid': 'template-email-tab-plain-text' },
  ];

  return (
    <div className="p-6">
      <div className="space-y-4 max-w-screen-2xl mx-auto">
        <Tabs tabs={tabs} activeTab={subTab} onChange={(id) => setSubTab(id as SubTab)} variant="pills" />

        {subTab === 'preview' ? (
          previewHtml.trim() ? (
            <div
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
              data-testid="template-email-preview-frame"
            >
              <EmailHtmlFrame html={previewHtml} />
            </div>
          ) : (
            <div
              className="p-8 rounded-lg text-center"
              style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
              data-testid="template-email-preview-empty"
            >
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                Nenhum conteúdo ainda. Cole o HTML na aba HTML ou use "Construir com IA".
              </p>
            </div>
          )
        ) : subTab === 'html' ? (
          <div className="p-4 rounded-lg" style={CAIXA}>
            <Textarea
              data-testid="template-email-html-input"
              value={html}
              onChange={(e) => onHtmlChange(e.target.value)}
              rows={24}
              spellCheck={false}
              className="font-mono"
              placeholder="Cole ou escreva o HTML do email. Use {{1}}, {{2}} para variáveis..."
            />
            <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
              CSS inline e tabelas funcionam melhor nos clientes de e-mail. O preview mostra o resultado com valores de exemplo.
            </p>
          </div>
        ) : (
          <div className="p-4 rounded-lg" style={CAIXA}>
            <Textarea
              data-testid="template-email-plain-text-input"
              value={plainText}
              onChange={(e) => onPlainTextChange(e.target.value)}
              rows={24}
              spellCheck={false}
              className="font-mono"
              placeholder="Versão em texto puro do email. Use {{1}}, {{2}} para variáveis..."
            />
            <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
              Exibido quando o cliente de e-mail não mostra HTML.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

EmailContentTab.displayName = 'EmailContentTab';
